import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminLayout } from '../components/AdminLayout';
import ImageKitUpload from '../job-board/components/ImageKitUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPES = ['article', 'signal', 'brief', 'magazine_feature', 'sponsored_content', 'editorial_note', 'cover_story']; // legacy
const STATUSES = ['draft', 'scheduled', 'published', 'archived', 'unlisted', 'deleted'];
const PILLARS = ['education', 'business-finance', 'government', 'healthcare', 'technology-ai', 'workforce-labor'];
const PILLAR_LABELS = {
  'education': 'Education',
  'business-finance': 'Business & Finance',
  'government': 'Government',
  'healthcare': 'Healthcare',
  'technology-ai': 'Technology & AI',
  'workforce-labor': 'Workforce & Labor',
};
const RAILS = ['standard', 'editors_choice', 'trending', 'opinion', 'politics', 'signal', 'magazine']; // deprecated
const AUDIO_STATUSES = ['none', 'pending', 'generated', 'published', 'failed'];
const AUDIO_GEN_STATUSES = ['none', 'queued', 'processing', 'generated', 'published', 'failed'];
const EXPIRED_VISIBILITY = ['archived_public', 'archived_members_only', 'unlisted'];
const VIEW_MODES = ['actual', 'display', 'hidden'];

// Phase 1B — Clean taxonomy constants
const CONTENT_FORMATS = ['article', 'magazine_feature', 'signal_brief', 'editorial_note', 'interview', 'roundup', 'sponsored_article', 'opportunity_roundup'];
const DISTRIBUTION_TARGETS = ['website', 'magazine', 'opportunity_signal'];
const WEBSITE_SECTIONS = ['general', 'education', 'business-finance', 'government', 'healthcare', 'technology-ai', 'workforce-labor', 'opportunity_signal'];
const SECTION_LABELS = {
  'general': 'General',
  'education': 'Education',
  'business-finance': 'Business & Finance',
  'government': 'Government',
  'healthcare': 'Healthcare',
  'technology-ai': 'Technology & AI',
  'workforce-labor': 'Workforce & Labor',
  'opportunity_signal': 'Opportunity Signal',
};
const MAGAZINE_STATUSES_LIST = ['not_eligible', 'eligible', 'assigned', 'published_in_issue'];
const EDITORIAL_PLACEMENTS = ['standard', 'featured', 'editors_choice', 'trending', 'cover_feature', 'sponsored_feature'];
const EDITORIAL_SLOTS = ['', 'hot_topic', 'editors_corner', 'opinion', 'staff_pick'];
const EDITORIAL_SLOT_LABELS = { '': '— None —', hot_topic: 'Hot Topic', editors_corner: "Editor's Corner", opinion: 'Opinion', staff_pick: 'Staff Pick' };
const EDITORIAL_TOPICS_LIST = ['opportunity_economy', 'career_mobility', 'workforce_development', 'healthcare_workforce', 'government_opportunity', 'business_growth', 'education_access', 'ai_workforce', 'policy_compliance', 'leadership', 'funding_capital', 'sponsored_content'];

function authHeader() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function CountdownBadge({ expiresAt, status }) {
  if (!expiresAt || status === 'archived' || status === 'deleted') return null;
  const diff = Math.ceil((new Date(expiresAt) - Date.now()) / 86400000);
  if (diff < 0) return <span className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-600">Expired</span>;
  if (diff === 0) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Expires today</span>;
  if (diff === 1) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Expires tomorrow</span>;
  if (diff <= 7) return <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">{diff}d left</span>;
  return null;
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-gray-100 text-gray-600',
    scheduled: 'bg-blue-100 text-blue-700',
    published: 'bg-green-100 text-green-700',
    archived: 'bg-yellow-100 text-yellow-700',
    unlisted: 'bg-purple-100 text-purple-700',
    deleted: 'bg-red-100 text-red-600',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-gray-100'}`}>{status}</span>;
}

// ── AI Draft Assistant Modal ───────────────────────────────────────────────

function AIDraftModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    prompt: '',
    primary_pillar: 'healthcare',
    content_format: 'article',
    distribution_targets: ['website'],
    magazine_status: 'not_eligible',
    desired_word_count: 700,
    tone: 'professional, practical, and optimistic',
    byline: 'Opportunity Post Editorial Team',
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleDistTarget = (target, checked) =>
    setForm(prev => ({
      ...prev,
      distribution_targets: checked
        ? [...prev.distribution_targets, target]
        : prev.distribution_targets.filter(t => t !== target),
    }));

  const handleGenerate = async () => {
    if (!form.prompt.trim()) { setError('Please enter a content brief.'); return; }
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/content/ai/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      onSuccess(data);
    } catch (err) {
      setError(err.message || 'Generation failed. Please try again.');
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-draft-modal">
      <div className="absolute inset-0 bg-black/50" onClick={!generating ? onClose : undefined} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900" data-testid="ai-modal-title">AI Draft Assistant</h2>
            <p className="text-xs text-gray-400 mt-0.5">Generates a draft saved to Content Hub — always status: draft</p>
          </div>
          {!generating && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              data-testid="ai-modal-close">×</button>
          )}
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* Brief */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Content Brief <span className="text-red-500">*</span></label>
            <textarea
              value={form.prompt}
              onChange={e => setForm(prev => ({ ...prev, prompt: e.target.value }))}
              disabled={generating}
              placeholder="Describe what the article should cover. Include the angle, audience, and key points you want addressed. The AI will not invent statistics or specific facts not mentioned here."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400"
              style={{ '--tw-ring-color': 'var(--op-teal)' }}
              rows={5}
              data-testid="ai-brief-input"
            />
          </div>

          {/* Pillar + Format */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Primary Pillar</label>
              <select
                value={form.primary_pillar}
                onChange={e => setForm(prev => ({ ...prev, primary_pillar: e.target.value }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-pillar-select"
              >
                {PILLARS.map(p => <option key={p} value={p}>{PILLAR_LABELS[p] || p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Content Format</label>
              <select
                value={form.content_format}
                onChange={e => setForm(prev => ({ ...prev, content_format: e.target.value }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-format-select"
              >
                {CONTENT_FORMATS.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          {/* Distribution */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Distribution Targets</label>
            <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              {DISTRIBUTION_TARGETS.map(target => (
                <label key={target} className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox"
                    checked={form.distribution_targets.includes(target)}
                    onChange={e => handleDistTarget(target, e.target.checked)}
                    disabled={generating}
                    className="w-4 h-4"
                    data-testid={`ai-dist-${target}`}
                  />
                  <span className="text-sm text-gray-700 capitalize">{target.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Length + Tone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target Length</label>
              <select
                value={form.desired_word_count}
                onChange={e => setForm(prev => ({ ...prev, desired_word_count: parseInt(e.target.value) }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-length-select"
              >
                <option value={300}>Short (~300 words)</option>
                <option value={500}>Medium (~500 words)</option>
                <option value={700}>Standard (~700 words)</option>
                <option value={1000}>Long (~1000 words)</option>
                <option value={1500}>Deep dive (~1500 words)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tone</label>
              <select
                value={form.tone}
                onChange={e => setForm(prev => ({ ...prev, tone: e.target.value }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-tone-select"
              >
                <option value="professional, practical, and optimistic">Professional &amp; Practical</option>
                <option value="conversational and accessible">Conversational &amp; Accessible</option>
                <option value="analytical and data-driven">Analytical &amp; Data-Driven</option>
                <option value="inspirational and forward-looking">Inspirational &amp; Forward-Looking</option>
                <option value="authoritative and educational">Authoritative &amp; Educational</option>
              </select>
            </div>
          </div>

          {/* Byline + Magazine status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Byline</label>
              <input type="text"
                value={form.byline}
                onChange={e => setForm(prev => ({ ...prev, byline: e.target.value }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-byline-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Magazine Status</label>
              <select
                value={form.magazine_status}
                onChange={e => setForm(prev => ({ ...prev, magazine_status: e.target.value }))}
                disabled={generating}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none disabled:bg-gray-50"
                data-testid="ai-magazine-status-select"
              >
                {MAGAZINE_STATUSES_LIST.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          {/* Safety note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
            <strong>AI Safety:</strong> The assistant will not invent statistics, named quotes, or specific factual claims. All generated content saves as <strong>draft</strong> — review before publishing.
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="ai-error-msg">
              {error}
            </div>
          )}

          {/* Generating state */}
          {generating && (
            <div className="flex items-center justify-center gap-3 py-4" data-testid="ai-generating-state">
              <svg className="animate-spin h-5 w-5" style={{ color: 'var(--op-teal)' }} viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span className="text-sm font-medium text-gray-600">Generating draft… this may take 10–30 seconds</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-1 pb-1">
            {!generating && (
              <button onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                data-testid="ai-cancel-btn">
                Cancel
              </button>
            )}
            <button
              onClick={handleGenerate}
              disabled={generating || !form.prompt.trim()}
              className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              style={{ backgroundColor: 'var(--op-navy)' }}
              data-testid="ai-generate-btn"
            >
              {generating ? 'Generating…' : 'Generate Draft →'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Content Hub Workspace Persistence ─────────────────────────────────────
const WORKSPACE_KEY = 'op_content_hub_workspace';
const DEFAULT_FILTERS = { status: '', content_format: '', primary_pillar: '', website_section: '', magazine_status: '', include_archived: false };

function readWorkspace() {
  try { return JSON.parse(sessionStorage.getItem(WORKSPACE_KEY) || 'null'); }
  catch { return null; }
}
function writeWorkspace(data) {
  try { sessionStorage.setItem(WORKSPACE_KEY, JSON.stringify(data)); }
  catch {}
}

// ── Slug → Month Year helper ───────────────────────────────────────────────
const SLUG_MONTH_MAP = {
  january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr',
  may: 'May', june: 'Jun', july: 'Jul', august: 'Aug',
  september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
};
function slugToMonthYear(slug) {
  if (!slug) return '—';
  const parts = slug.split('-');
  if (parts.length >= 2) {
    const month = SLUG_MONTH_MAP[parts[0].toLowerCase()];
    const year = parts[parts.length - 1];
    if (month && /^\d{4}$/.test(year)) return `${month} ${year}`;
  }
  return slug;
}

// ── Column definitions (order-agnostic) ───────────────────────────────────
const COLUMNS_DEF = [
  { key: 'title',                  label: 'Title' },
  { key: 'me',                     label: 'ME' },
  { key: 'nl',                     label: 'NL' },
  { key: 'org',                    label: 'ORG' },
  { key: 'content_format',         label: 'Format' },
  { key: 'primary_pillar',         label: 'Pillar' },
  { key: 'editorial_placement',    label: 'Placement' },
  { key: 'status',                 label: 'Status' },
  { key: 'magazine_issue_slug',    label: 'Issue' },
  { key: 'estimated_read_minutes', label: 'Read',       right: true },
  { key: 'audio_status',           label: 'Audio' },
  { key: 'audio_generated_at',     label: 'Audio Date' },
  { key: 'display_view_count',     label: 'Reads',      right: true },
  { key: 'actual_view_count',      label: 'Views',      right: true },
  { key: 'expires_at',             label: 'Expires' },
  { key: 'published_at',           label: 'Published' },
  { key: 'created_at',             label: 'Created' },
];
const DEFAULT_COL_ORDER = COLUMNS_DEF.map(c => c.key);
const COL_ORDER_KEY    = 'op_content_hub_col_order';
const COL_HIDDEN_KEY   = 'op_content_hub_hidden_cols';
const COL_WIDTHS_KEY   = 'op_content_hub_col_widths';

const DEFAULT_HIDDEN_COLS = ['audio_generated_at', 'actual_view_count', 'display_view_count'];

const DEFAULT_COL_WIDTHS = {
  title: 280, me: 56, nl: 56, org: 72, content_format: 110,
  primary_pillar: 130, editorial_placement: 110, status: 90,
  magazine_issue_slug: 90, estimated_read_minutes: 68, audio_status: 80,
  audio_generated_at: 100, display_view_count: 72, actual_view_count: 68,
  expires_at: 100, published_at: 96, created_at: 96,
};

function readColOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(COL_ORDER_KEY));
    if (Array.isArray(saved) && saved.length > 0) {
      const missing = DEFAULT_COL_ORDER.filter(k => !saved.includes(k));
      return [...saved.filter(k => DEFAULT_COL_ORDER.includes(k)), ...missing];
    }
  } catch {}
  return [...DEFAULT_COL_ORDER];
}
function writeColOrder(order) {
  try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(order)); } catch {}
}
function readHiddenCols() {
  try {
    const s = localStorage.getItem(COL_HIDDEN_KEY);
    if (s !== null) return JSON.parse(s);
  } catch {}
  return [...DEFAULT_HIDDEN_COLS];
}
function writeHiddenCols(cols) {
  try { localStorage.setItem(COL_HIDDEN_KEY, JSON.stringify(cols)); } catch {}
}
function readColWidths() {
  try {
    const s = localStorage.getItem(COL_WIDTHS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}
function writeColWidths(widths) {
  try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(widths)); } catch {}
}

// ── Sortable + Draggable + Resizable column header ────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort, right = false,
                  onDragStart, onDragOver, onDrop, onDragEnd, isDragOver, isDragging,
                  width, onResizeStart }) {
  const active = sortCol === col;
  return (
    <th
      draggable
      className={`font-medium text-gray-600 select-none whitespace-nowrap transition-colors
        ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-400' : 'hover:bg-gray-100'}
        ${isDragging ? 'opacity-40' : 'opacity-100'}
      `}
      onClick={() => onSort(col)}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`sort-col-${col}`}
      style={{
        width: width || undefined,
        minWidth: 40,
        maxWidth: width || undefined,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
    >
      <div className={`px-3 py-3 flex items-center gap-1 overflow-hidden ${right ? 'flex-row-reverse' : ''}`}>
        <span className="text-gray-300 text-xs leading-none flex-shrink-0" title="Drag to reorder">⠿</span>
        <span className="truncate">{label}</span>
        <span className="text-gray-400 text-xs flex-shrink-0">{active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </div>
      {/* Resize handle — sits at right edge, prevents drag from firing */}
      <div
        draggable={false}
        title="Drag to resize"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
          const currentWidth = e.currentTarget.closest('th').offsetWidth;
          onResizeStart && onResizeStart(e.clientX, currentWidth);
        }}
        style={{
          position: 'absolute', right: 0, top: 0, width: 6, height: '100%',
          cursor: 'col-resize', zIndex: 10,
          background: 'transparent',
        }}
      />
    </th>
  );
}

const FONT_SIZE_STEPS = ['text-xs', 'text-sm', 'text-base'];
const FONT_SIZE_KEY = 'op_content_hub_font_size';
function readFontSize() {
  try { const i = parseInt(localStorage.getItem(FONT_SIZE_KEY), 10); return isNaN(i) ? 0 : Math.min(Math.max(i, 0), 2); }
  catch { return 0; }
}

// ── One-time magazine backfill button ─────────────────────────────────────
function BackfillButton() {
  const [state, setState] = useState('idle'); // idle | running | done | error
  const [result, setResult] = useState(null);

  const run = async () => {
    if (state === 'running') return;
    setState('running');
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/backfill-magazine-flags`, {
        method: 'POST',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResult(data);
      setState('done');
    } catch (e) {
      setResult({ error: e.message });
      setState('error');
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="backfill-magazine-btn"
        onClick={run}
        disabled={state === 'running'}
        className={`px-3 py-2 text-xs rounded-lg border font-medium transition-colors whitespace-nowrap
          ${state === 'done' ? 'border-green-300 text-green-700 bg-green-50'
          : state === 'error' ? 'border-red-300 text-red-700 bg-red-50'
          : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
      >
        {state === 'running' ? 'Running…' : state === 'done' ? 'Backfill ✓' : state === 'error' ? 'Backfill ✗' : 'Run Magazine Backfill'}
      </button>
      {result && state === 'done' && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 w-64 text-xs text-gray-700">
          <p className="font-semibold mb-1 text-green-700">Backfill complete</p>
          <p>Linked articles: <b>{result.total_linked_in_magazine_pages}</b></p>
          <p>me=True set: <b>{result.verification?.me_true}</b></p>
          <p>status=published: <b>{result.verification?.status_published}</b></p>
          <p>Issue slug set: <b>{result.verification?.magazine_issue_slug_populated}</b></p>
          <p>magazine_status=assigned: <b>{result.verification?.magazine_status_assigned}</b></p>
          <button onClick={() => { setState('idle'); setResult(null); }} className="mt-2 text-gray-400 underline">dismiss</button>
        </div>
      )}
      {result && state === 'error' && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-red-200 rounded-lg shadow-lg z-50 p-3 w-48 text-xs text-red-700">
          Error: {result.error}
          <button onClick={() => { setState('idle'); setResult(null); }} className="ml-2 underline">dismiss</button>
        </div>
      )}
    </div>
  );
}


export function ContentLibraryList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // wsOnMount: read sessionStorage once so both useState and scroll-restore share the same snapshot
  const wsOnMount = useRef(readWorkspace());
  const [filters, setFilters] = useState(() => wsOnMount.current?.filters ?? DEFAULT_FILTERS);
  const [showAIModal, setShowAIModal] = useState(false);
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  // Persist filters to sessionStorage on every change (preserves saved scrollY)
  useEffect(() => {
    const current = readWorkspace();
    writeWorkspace({ scrollY: current?.scrollY ?? 0, filters });
  }, [filters]);

  // Restore scroll position after items finish loading (consumed once per mount)
  useEffect(() => {
    if (!loading && wsOnMount.current?.scrollY) {
      const y = wsOnMount.current.scrollY;
      wsOnMount.current = null; // consume so re-fetches don't re-scroll
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading]);

  // Save workspace (incl. scroll) before navigating to editor
  const handleEdit = (id) => {
    writeWorkspace({ filters, scrollY: window.scrollY });
    navigate(`/admin/content/${id}/edit`);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.content_format) params.set('content_format', filters.content_format);
      if (filters.primary_pillar) params.set('primary_pillar', filters.primary_pillar);
      if (filters.website_section) params.set('website_section', filters.website_section);
      if (filters.magazine_status) params.set('magazine_status', filters.magazine_status);
      if (filters.include_archived) params.set('include_archived', 'true');
      params.set('limit', '200');
      const res = await fetch(`${API}/api/content?${params}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to fetch');
      setItems(await res.json());
    } catch {
      toast.error('Failed to load content items');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleArchive = async (id) => {
    try {
      const res = await fetch(`${API}/api/content/${id}/archive`, { method: 'POST', headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success('Archived');
      fetchItems();
    } catch { toast.error('Archive failed'); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Soft-delete "${title}"? The record is preserved internally.`)) return;
    try {
      const res = await fetch(`${API}/api/content/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success('Soft-deleted');
      fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  const handleAIDraftSuccess = (item) => {
    setShowAIModal(false);
    toast.success(`Draft "${item.title}" created — opening editor`);
    fetchItems();
    writeWorkspace({ filters, scrollY: 0 });
    navigate(`/admin/content/${item.id}/edit`);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const displayItems = useMemo(() => {
    let result = [...items];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(item =>
        (item.title || '').toLowerCase().includes(q) ||
        (item.slug || '').toLowerCase().includes(q) ||
        (item.source_name || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let av = a[sortCol];
      let bv = b[sortCol];
      const nullVal = sortDir === 'asc' ? '\uFFFF' : '';
      if (av == null || av === '') av = nullVal;
      if (bv == null || bv === '') bv = nullVal;
      if (typeof av === 'boolean') av = av ? 1 : 0;
      if (typeof bv === 'boolean') bv = bv ? 1 : 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [items, search, sortCol, sortDir]);

  // ── Column drag-and-drop ──────────────────────────────────────────────────
  const [colOrder, setColOrder] = useState(readColOrder);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragColRef = useRef(null);

  const handleDragStart = (col) => (e) => {
    dragColRef.current = col;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (col) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(col);
  };
  const handleDrop = (col) => (e) => {
    e.preventDefault();
    const from = dragColRef.current;
    if (!from || from === col) { setDragOverCol(null); return; }
    setColOrder(prev => {
      const next = [...prev];
      const fi = next.indexOf(from);
      const ti = next.indexOf(col);
      next.splice(fi, 1);
      next.splice(ti, 0, from);
      writeColOrder(next);
      return next;
    });
    dragColRef.current = null;
    setDragOverCol(null);
  };
  const handleDragEnd = () => {
    dragColRef.current = null;
    setDragOverCol(null);
  };

  const [fontSizeIdx, setFontSizeIdx] = useState(readFontSize);
  const changeFontSize = (delta) => setFontSizeIdx(prev => {
    const next = Math.min(Math.max(prev + delta, 0), FONT_SIZE_STEPS.length - 1);
    try { localStorage.setItem(FONT_SIZE_KEY, next); } catch {}
    return next;
  });
  const [hiddenCols, setHiddenCols] = useState(readHiddenCols);
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  useEffect(() => {
    if (!showColPicker) return;
    const handler = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColPicker]);

  const toggleHiddenCol = (key) => {
    setHiddenCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      writeHiddenCols(next);
      return next;
    });
  };

  // ── Column resizing ───────────────────────────────────────────────────────
  const [colWidths, setColWidths] = useState(readColWidths);
  const colWidthsRef = useRef(colWidths);
  useEffect(() => { colWidthsRef.current = colWidths; }, [colWidths]);
  const resizeRef = useRef(null);

  const startResize = (col, clientX, currentWidth) => {
    resizeRef.current = { col, startX: clientX, startW: currentWidth };

    const moveHandler = (e) => {
      if (!resizeRef.current) return;
      const newW = Math.max(40, resizeRef.current.startW + (e.clientX - resizeRef.current.startX));
      setColWidths(prev => ({ ...prev, [resizeRef.current.col]: newW }));
    };
    const upHandler = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
      writeColWidths(colWidthsRef.current);
    };
    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  };

  // ── Cell renderer ─────────────────────────────────────────────────────────
  const renderCell = (item, colKey) => {
    switch (colKey) {
      case 'title': return (
        <td key="title" className="px-4 py-3">
          <div className="font-medium text-gray-900 max-w-xs truncate">{item.title}</div>
        </td>
      );
      case 'me': return (
        <td key="me" className="px-4 py-3 text-center">
          {item.me
            ? <span className="text-green-600 font-bold text-sm" data-testid={`me-badge-${item.id}`}>✓</span>
            : <span className="text-gray-300 text-xs">—</span>}
        </td>
      );
      case 'nl': return (
        <td key="nl" className="px-4 py-3 text-center">
          {item.nl
            ? <span className="text-amber-600 font-bold text-sm" data-testid={`nl-badge-${item.id}`}>✓</span>
            : <span className="text-gray-300 text-xs">—</span>}
        </td>
      );
      case 'org': return (
        <td key="org" className="px-4 py-3 text-xs text-gray-600 font-mono">{item.org || 'OP'}</td>
      );
      case 'content_format': return (
        <td key="content_format" className="px-4 py-3 text-gray-600">{item.content_format || item.content_type}</td>
      );
      case 'primary_pillar': return (
        <td key="primary_pillar" className="px-4 py-3 text-gray-600">{item.primary_pillar || '—'}</td>
      );
      case 'editorial_placement': return (
        <td key="editorial_placement" className="px-4 py-3 text-gray-600">{item.editorial_placement || item.editorial_rail || '—'}</td>
      );
      case 'status': return (
        <td key="status" className="px-4 py-3"><StatusBadge status={item.status} /></td>
      );
      case 'magazine_issue_slug': return (
        <td key="magazine_issue_slug" className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{slugToMonthYear(item.magazine_issue_slug)}</td>
      );
      case 'estimated_read_minutes': return (
        <td key="estimated_read_minutes" className="px-4 py-3 text-right text-gray-600">
          {item.estimated_read_minutes ? `${item.estimated_read_minutes}m` : '—'}
        </td>
      );
      case 'audio_status': return (
        <td key="audio_status" className="px-4 py-3">
          {item.audio_enabled
            ? <span className="px-2 py-0.5 rounded text-xs bg-teal-100 text-teal-700">{item.audio_status}</span>
            : <span className="text-gray-300 text-xs">—</span>}
        </td>
      );
      case 'audio_generated_at': return (
        <td key="audio_generated_at" className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
          {item.audio_generated_at ? new Date(item.audio_generated_at).toLocaleDateString() : '—'}
        </td>
      );
      case 'display_view_count': return (
        <td key="display_view_count" className="px-4 py-3 text-right text-gray-600">{item.display_view_count ?? 0}</td>
      );
      case 'actual_view_count': return (
        <td key="actual_view_count" className="px-4 py-3 text-right text-gray-500 text-xs">{item.actual_view_count ?? 0}</td>
      );
      case 'expires_at': return (
        <td key="expires_at" className="px-4 py-3">
          <div className="flex flex-col gap-0.5">
            {item.expires_at && <span className="text-xs text-gray-500">{new Date(item.expires_at).toLocaleDateString()}</span>}
            <CountdownBadge expiresAt={item.expires_at} status={item.status} />
          </div>
        </td>
      );
      case 'published_at': return (
        <td key="published_at" className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
          {item.published_at ? new Date(item.published_at).toLocaleDateString() : '—'}
        </td>
      );
      case 'created_at': return (
        <td key="created_at" className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
        </td>
      );
      default: return <td key={colKey} />;
    }
  };


  return (
    <AdminLayout title="Editorial" breadcrumb="Content Hub">
      <div style={{ minHeight: '100vh', background: '#F0EDE7' }}>
      {/* AI Draft Modal */}
      {showAIModal && (
        <AIDraftModal
          onClose={() => setShowAIModal(false)}
          onSuccess={handleAIDraftSuccess}
        />
      )}
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" style={{ marginBottom: 0 }}>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Editorial Content Hub</h1>
          <p className="text-sm text-gray-500 mt-0.5">Unified content engine — articles, signals, magazine features</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Magazine backfill */}
          <BackfillButton />
          {/* Font size controls */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              data-testid="font-size-decrease-btn"
              onClick={() => changeFontSize(-1)}
              disabled={fontSizeIdx === 0}
              className="px-2.5 py-2 text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed border-r border-gray-200"
              title="Decrease font size"
            >A−</button>
            <button
              data-testid="font-size-increase-btn"
              onClick={() => changeFontSize(1)}
              disabled={fontSizeIdx === FONT_SIZE_STEPS.length - 1}
              className="px-2.5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Increase font size"
            >A+</button>
          </div>
          {/* Columns visibility picker */}
          <div className="relative" ref={colPickerRef}>
            <button
              data-testid="columns-picker-btn"
              onClick={() => setShowColPicker(v => !v)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 flex items-center gap-1.5"
            >
              Columns {hiddenCols.length > 0 && <span className="text-xs bg-gray-200 rounded-full px-1.5">{COLUMNS_DEF.length - hiddenCols.length}/{COLUMNS_DEF.length}</span>}
            </button>
            {showColPicker && (
              <div
                className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 w-52"
                data-testid="columns-picker-panel"
              >
                <p className="text-xs text-gray-500 mb-2 font-medium">Show / hide columns</p>
                {COLUMNS_DEF.map(col => (
                  <label key={col.key} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                    <input
                      type="checkbox"
                      checked={!hiddenCols.includes(col.key)}
                      onChange={() => toggleHiddenCol(col.key)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">{col.label}</span>
                  </label>
                ))}
                <button
                  onClick={() => { setHiddenCols([...DEFAULT_HIDDEN_COLS]); writeHiddenCols([...DEFAULT_HIDDEN_COLS]); }}
                  className="mt-2 text-xs text-gray-400 hover:text-gray-600 w-full text-left"
                >
                  Reset to defaults
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="ai-draft-btn"
            onClick={() => setShowAIModal(true)}
            className="px-4 py-2 text-sm font-medium border rounded-lg hover:opacity-90 transition-opacity flex items-center gap-1.5"
            style={{ borderColor: 'var(--op-teal)', color: 'var(--op-teal)' }}
          >
            <span style={{ fontSize: '14px' }}>✦</span> AI Draft
          </button>
          <button
            data-testid="new-content-btn"
            onClick={() => { writeWorkspace({ filters, scrollY: window.scrollY }); navigate('/admin/content/new'); }}
            className="px-4 py-2 text-sm bg-navy-900 text-white rounded-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--op-navy, #1a2744)' }}
          >
            + New Content Item
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-100 px-6 py-3 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          data-testid="content-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search title, slug, source…"
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700 w-56 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <select data-testid="filter-status" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-format" value={filters.content_format} onChange={e => setFilters(f => ({ ...f, content_format: e.target.value }))}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
          <option value="">All Formats</option>
          {CONTENT_FORMATS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select data-testid="filter-pillar" value={filters.primary_pillar} onChange={e => setFilters(f => ({ ...f, primary_pillar: e.target.value }))}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
          <option value="">All Pillars</option>
          {PILLARS.map(p => <option key={p} value={p}>{PILLAR_LABELS[p] || p}</option>)}
        </select>
        <select data-testid="filter-section" value={filters.website_section} onChange={e => setFilters(f => ({ ...f, website_section: e.target.value }))}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
          <option value="">All Sections</option>
          {WEBSITE_SECTIONS.map(s => <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>)}
        </select>
        <select data-testid="filter-mag-status" value={filters.magazine_status} onChange={e => setFilters(f => ({ ...f, magazine_status: e.target.value }))}
          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white text-gray-700">
          <option value="">All Mag Status</option>
          {MAGAZINE_STATUSES_LIST.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={filters.include_archived}
            onChange={e => setFilters(f => ({ ...f, include_archived: e.target.checked }))} />
          Show Archived
        </label>
        <button onClick={fetchItems} className="text-sm px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50">
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading content items…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg font-medium">No content items found</p>
            <p className="text-sm mt-1">Create your first content item to get started.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className={`w-full ${FONT_SIZE_STEPS[fontSizeIdx]}`} data-testid="content-table" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {colOrder.filter(k => !hiddenCols.includes(k)).map(colKey => {
                      const col = COLUMNS_DEF.find(c => c.key === colKey);
                      if (!col) return null;
                      return (
                        <SortTh
                          key={col.key}
                          col={col.key}
                          label={col.label}
                          sortCol={sortCol}
                          sortDir={sortDir}
                          onSort={handleSort}
                          right={col.right}
                          width={colWidths[col.key]}
                          onResizeStart={(clientX, currentWidth) => startResize(col.key, clientX, currentWidth)}
                          isDragOver={dragOverCol === col.key}
                          isDragging={dragColRef.current === col.key}
                          onDragStart={handleDragStart(col.key)}
                          onDragOver={handleDragOver(col.key)}
                          onDrop={handleDrop(col.key)}
                          onDragEnd={handleDragEnd}
                        />
                      );
                    })}
                    <th className="text-left px-3 py-3 font-medium text-gray-600 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map(item => (
                    <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" data-testid={`content-row-${item.id}`}>
                      {colOrder.filter(k => !hiddenCols.includes(k)).map(colKey => renderCell(item, colKey))}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            data-testid={`edit-btn-${item.id}`}
                            onClick={() => handleEdit(item.id)}
                            className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                          >
                            Edit
                          </button>
                          {item.status === 'published' && (
                            <button
                              data-testid={`archive-btn-${item.id}`}
                              onClick={() => handleArchive(item.id)}
                              className="text-xs px-2 py-1 border border-amber-200 text-amber-700 rounded hover:bg-amber-50 transition-colors"
                            >
                              Archive
                            </button>
                          )}
                          {item.status !== 'deleted' && (
                            <button
                              data-testid={`delete-btn-${item.id}`}
                              onClick={() => handleDelete(item.id, item.title)}
                              className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between">
              <span>{displayItems.length}{displayItems.length !== items.length ? ` of ${items.length}` : ''} item{items.length !== 1 ? 's' : ''} — soft-delete preserves records internally</span>
              <button
                onClick={() => {
                  const o = [...DEFAULT_COL_ORDER]; writeColOrder(o); setColOrder(o);
                  const w = { ...DEFAULT_COL_WIDTHS }; writeColWidths(w); setColWidths(w);
                  const h = [...DEFAULT_HIDDEN_COLS]; writeHiddenCols(h); setHiddenCols(h);
                }}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
                data-testid="reset-col-order-btn"
              >
                Reset all column preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}

// ── Editor ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '', slug: '', dek: '', summary: '', body: '', content_type: 'article', status: 'draft',
  byline: '', author_id: '', section: '', department: '', primary_pillar: '', editorial_rail: '',
  anchor_taxonomy: '', tags: '', is_editors_choice: false, is_trending: false,
  trending_score: '', homepage_featured_rank: '',
  featured_image_url: '', hero_image_url: '', image_caption: '', image_credit: '',
  audio_enabled: false, audio_status: 'none', audio_url: '', audio_provider: '',
  audio_transcript: '', audio_voice_id: '', audio_auto_generate: false,
  audio_generation_provider: '', audio_source_text: '', audio_job_id: '', audio_error: '',
  audio_duration_seconds: '',
  sponsor_name: '', sponsor_id: '', sponsor_disclosure: '', is_sponsored: false,
  seo_title: '', seo_description: '', canonical_url: '', og_image_url: '',
  scheduled_for: '', live_window_days: 7, show_countdown: true, countdown_start_days: 7,
  expired_visibility: 'archived_public',
  view_count_mode: 'display', momentum_enabled: false,
  daily_display_increment_min: 0, daily_display_increment_max: 0,
  display_view_count: 0, actual_view_count: 0, view_boost_count: 0,
  issue_ids: '', related_content_ids: '',
  // Phase 1B — clean taxonomy
  content_format: 'article',
  distribution_targets: [],
  website_section: '',
  magazine_status: 'not_eligible',
  editorial_placement: 'standard',
  editorial_topics: [],
  editorial_slot: '',
  // Flags / Codes
  me: false,
  nl: false,
  org: 'OP',
};

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 pb-2 border-b border-gray-100">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      {...rest}
    />
  );
}

function Select({ value, onChange, children, ...rest }) {
  return (
    <select
      value={value ?? ''}
      onChange={onChange}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      {...rest}
    >
      {children}
    </select>
  );
}

function Textarea({ value, onChange, rows = 4, placeholder }) {
  return (
    <textarea
      value={value ?? ''}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
    />
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-teal-500' : 'bg-gray-300'}`}
        onClick={() => onChange(!checked)}>
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

export function ContentLibraryEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [readMetrics, setReadMetrics] = useState({ word_count: 0, estimated_read_minutes: 1 });

  useEffect(() => {
    if (!isEdit) return;
    fetch(`${API}/api/content/${id}`, { headers: authHeader() })
      .then(r => r.json())
      .then(data => {
        setForm({
          ...EMPTY_FORM,
          ...data,
          tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
          anchor_taxonomy: Array.isArray(data.anchor_taxonomy) ? data.anchor_taxonomy.join(', ') : '',
          issue_ids: Array.isArray(data.issue_ids) ? data.issue_ids.join(', ') : '',
          related_content_ids: Array.isArray(data.related_content_ids) ? data.related_content_ids.join(', ') : '',
          audio_duration_seconds: data.audio_duration_seconds ?? '',
          trending_score: data.trending_score ?? '',
          homepage_featured_rank: data.homepage_featured_rank ?? '',
          scheduled_for: data.scheduled_for ? new Date(data.scheduled_for).toISOString().slice(0, 16) : '',
          editorial_slot: data.editorial_slot ?? '',
          me: data.me ?? false,
          nl: data.nl ?? false,
          org: data.org ?? 'OP',
        });
        setReadMetrics({ word_count: data.word_count || 0, estimated_read_minutes: data.estimated_read_minutes || 1 });
        setLoading(false);
      })
      .catch(() => { toast.error('Failed to load content item'); setLoading(false); });
  }, [id, isEdit]);

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const b = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

  const autoSlug = () => {
    if (!form.slug && form.title) {
      const slug = form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setForm(prev => ({ ...prev, slug }));
    }
  };

  const parseList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: parseList(form.tags),
        anchor_taxonomy: parseList(form.anchor_taxonomy),
        issue_ids: parseList(form.issue_ids),
        related_content_ids: parseList(form.related_content_ids),
        audio_duration_seconds: form.audio_duration_seconds !== '' ? parseInt(form.audio_duration_seconds) || null : null,
        trending_score: form.trending_score !== '' ? parseFloat(form.trending_score) || null : null,
        homepage_featured_rank: form.homepage_featured_rank !== '' ? parseInt(form.homepage_featured_rank) || null : null,
        live_window_days: parseInt(form.live_window_days) || 7,
        countdown_start_days: parseInt(form.countdown_start_days) || 7,
        daily_display_increment_min: parseInt(form.daily_display_increment_min) || 0,
        daily_display_increment_max: parseInt(form.daily_display_increment_max) || 0,
        view_boost_count: parseInt(form.view_boost_count) || 0,
        scheduled_for: form.scheduled_for || null,
      };
      // Remove computed/auto fields from create
      if (!isEdit) {
        delete payload.id;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload.word_count;
        delete payload.estimated_read_minutes;
        delete payload.estimated_read_seconds;
        delete payload.published_at;
        delete payload.expires_at;
        delete payload.archived_at;
        delete payload.deleted_at;
        delete payload.deleted_by;
        delete payload.actual_view_count;
        delete payload.display_view_count;
        delete payload.last_momentum_increment_at;
        delete payload.audio_job_id;
        delete payload.audio_error;
        delete payload.audio_generated_at;
        delete payload.audio_published_at;
        delete payload.audio_generation_status;
      }
      const url = isEdit ? `${API}/api/content/${id}` : `${API}/api/content`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Save failed');
      }
      const saved = await res.json();
      setReadMetrics({ word_count: saved.word_count || 0, estimated_read_minutes: saved.estimated_read_minutes || 1 });
      toast.success(isEdit ? 'Content item updated' : 'Content item created');
      if (!isEdit) navigate(`/admin/content/${saved.id}/edit`);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAudioAction = async (action) => {
    try {
      const res = await fetch(`${API}/api/content/${id}/audio/${action}`, { method: 'POST', headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success(`Audio ${action} triggered`);
    } catch { toast.error(`Audio ${action} failed`); }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400">Loading…</div>;

  return (
    <AdminLayout title="Editorial" breadcrumb={isEdit ? 'Edit Content Item' : 'New Content Item'}>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{isEdit ? 'Edit Content Item' : 'New Content Item'}</h1>
          {readMetrics.word_count > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">{readMetrics.word_count} words · {readMetrics.estimated_read_minutes} min read</p>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={() => navigate('/admin/content')} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            Back to Library
          </button>
          <button
            data-testid="save-content-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ backgroundColor: 'var(--op-navy, #1a2744)' }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Item'}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

          {/* ─── LEFT COLUMN: Identity · Audio · Sponsor · Views ─── */}
          <div>

            {/* Identity */}
            <Section title="Identity">
              <Field label="Title *">
                <Input value={form.title} onChange={f('title')} onBlur={autoSlug} placeholder="Article title" data-testid="content-title-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug" hint="Auto-generated from title. Must be unique.">
                  <Input value={form.slug} onChange={f('slug')} placeholder="url-friendly-slug" data-testid="content-slug-input" />
                </Field>
                <Field label="Content Type">
                  <Select value={form.content_type} onChange={f('content_type')} data-testid="content-type-select">
                    {CONTENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Status">
                  <Select value={form.status} onChange={f('status')} data-testid="content-status-select">
                    {STATUSES.filter(s => s !== 'deleted').map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field label="Byline">
                  <Input value={form.byline} onChange={f('byline')} placeholder="Author name or team" />
                </Field>
              </div>
              <Field label="Dek / Subtitle">
                <Input value={form.dek} onChange={f('dek')} placeholder="One-line summary shown under headline" />
              </Field>
              <Field label="Summary" hint="2–3 sentences for cards and SEO previews.">
                <Textarea value={form.summary} onChange={f('summary')} rows={2} placeholder="Summary for cards and feeds" />
              </Field>
              <Field label="Body (HTML)" hint={`Word count: ${readMetrics.word_count} · Est. read: ${readMetrics.estimated_read_minutes} min`}>
                <Textarea value={form.body} onChange={f('body')} rows={12} placeholder="<p>Full article body HTML…</p>" data-testid="content-body-input" />
              </Field>
            </Section>

            {/* Audio */}
            <Section title="Audio">
              <div className="flex gap-6 mb-4">
                <Toggle checked={form.audio_enabled} onChange={b('audio_enabled')} label="Audio Enabled" />
                <Toggle checked={form.audio_auto_generate} onChange={b('audio_auto_generate')} label="Auto-Generate" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Audio Status">
                  <Select value={form.audio_status} onChange={f('audio_status')}>
                    {AUDIO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </Select>
                </Field>
                <Field label="Audio Provider">
                  <Select value={form.audio_provider || ''} onChange={f('audio_provider')}>
                    <option value="">— None —</option>
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="manual">Manual</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
                <Field label="Audio URL">
                  <Input value={form.audio_url} onChange={f('audio_url')} placeholder="https://…/audio.mp3" />
                </Field>
                <Field label="Duration (seconds)">
                  <Input value={form.audio_duration_seconds} onChange={f('audio_duration_seconds')} type="number" placeholder="180" />
                </Field>
                <Field label="Voice ID">
                  <Input value={form.audio_voice_id} onChange={f('audio_voice_id')} placeholder="ElevenLabs voice ID" />
                </Field>
                <Field label="Generation Provider">
                  <Select value={form.audio_generation_provider || ''} onChange={f('audio_generation_provider')}>
                    <option value="">— None —</option>
                    <option value="elevenlabs">ElevenLabs</option>
                    <option value="manual">Manual</option>
                  </Select>
                </Field>
              </div>
              <Field label="Audio Transcript">
                <Textarea value={form.audio_transcript} onChange={f('audio_transcript')} rows={3} placeholder="Transcript text…" />
              </Field>
              <Field label="Audio Source Text" hint="Text sent to TTS engine. Defaults to body if empty.">
                <Textarea value={form.audio_source_text} onChange={f('audio_source_text')} rows={3} placeholder="Custom TTS source text…" />
              </Field>
              {isEdit && (
                <div className="flex gap-2 pt-2">
                  <button onClick={() => handleAudioAction('generate')} className="text-xs px-3 py-1.5 border border-teal-300 text-teal-700 rounded hover:bg-teal-50 transition-colors" data-testid="audio-generate-btn">Queue Generate</button>
                  <button onClick={() => handleAudioAction('publish')} className="text-xs px-3 py-1.5 border border-green-300 text-green-700 rounded hover:bg-green-50 transition-colors" data-testid="audio-publish-btn">Publish Audio</button>
                  <button onClick={() => handleAudioAction('remove')} className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50 transition-colors" data-testid="audio-remove-btn">Remove Audio</button>
                </div>
              )}
            </Section>

            {/* Sponsor / Disclosure */}
            <Section title="Sponsor / Disclosure">
              <Toggle checked={form.is_sponsored} onChange={b('is_sponsored')} label="Sponsored Content" />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Field label="Sponsor Name">
                  <Input value={form.sponsor_name} onChange={f('sponsor_name')} placeholder="Sponsor organization" />
                </Field>
                <Field label="Sponsor ID">
                  <Input value={form.sponsor_id} onChange={f('sponsor_id')} placeholder="Internal sponsor ID" />
                </Field>
              </div>
              <Field label="Disclosure Statement">
                <Textarea value={form.sponsor_disclosure} onChange={f('sponsor_disclosure')} rows={2} placeholder="e.g. Sponsored content from XYZ…" />
              </Field>
            </Section>

            {/* Views / Reads / Momentum */}
            <Section title="Views / Reads / Momentum">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Display Reads (public)" hint="Shown to readers as 'reads'">
                  <Input value={form.display_view_count} onChange={f('display_view_count')} type="number" />
                </Field>
                <Field label="Actual Views (internal)">
                  <Input value={form.actual_view_count} onChange={f('actual_view_count')} type="number" />
                </Field>
                <Field label="Boost Count (admin add)">
                  <Input value={form.view_boost_count} onChange={f('view_boost_count')} type="number" />
                </Field>
                <Field label="View Count Mode">
                  <Select value={form.view_count_mode} onChange={f('view_count_mode')}>
                    {VIEW_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </Select>
                </Field>
                <Field label="Increment Min/day">
                  <Input value={form.daily_display_increment_min} onChange={f('daily_display_increment_min')} type="number" />
                </Field>
                <Field label="Increment Max/day">
                  <Input value={form.daily_display_increment_max} onChange={f('daily_display_increment_max')} type="number" />
                </Field>
              </div>
              <Toggle checked={form.momentum_enabled} onChange={b('momentum_enabled')} label="Enable Daily Momentum Increment" />
            </Section>

          </div>{/* end left column */}

          {/* ─── RIGHT COLUMN: Images · Core Taxonomy · SEO · Lifecycle ─── */}
          <div>

            {/* Images */}
            <Section title="Images">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Featured Image URL">
                  <Input value={form.featured_image_url} onChange={f('featured_image_url')} placeholder="https://ik.imagekit.io/…" />
                  <div style={{ marginTop: 6 }}>
                    <ImageKitUpload
                      folder="uploads/"
                      accept="image/*"
                      label="Upload Featured Image"
                      token={localStorage.getItem('auth_token')}
                      onUploaded={(url) => setForm(prev => ({ ...prev, featured_image_url: url }))}
                    />
                  </div>
                </Field>
                <Field label="Hero Image URL">
                  <Input value={form.hero_image_url} onChange={f('hero_image_url')} placeholder="https://ik.imagekit.io/…" />
                  <div style={{ marginTop: 6 }}>
                    <ImageKitUpload
                      folder="uploads/"
                      accept="image/*"
                      label="Upload Hero Image"
                      token={localStorage.getItem('auth_token')}
                      onUploaded={(url) => setForm(prev => ({ ...prev, hero_image_url: url }))}
                    />
                  </div>
                </Field>
                <Field label="Image Caption">
                  <Input value={form.image_caption} onChange={f('image_caption')} placeholder="Caption text" />
                </Field>
                <Field label="Image Credit">
                  <Input value={form.image_credit} onChange={f('image_credit')} placeholder="Photographer / Source" />
                </Field>
              </div>
            </Section>

            {/* Core Taxonomy */}
            <Section title="Core Taxonomy">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Content Format">
                  <Select value={form.content_format} onChange={f('content_format')} data-testid="content-format-select">
                    {CONTENT_FORMATS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </Select>
                </Field>
                <Field label="Primary Pillar">
                  <Select value={form.primary_pillar} onChange={f('primary_pillar')} data-testid="primary-pillar-select">
                    <option value="">— None —</option>
                    {PILLARS.map(p => <option key={p} value={p}>{PILLAR_LABELS[p] || p}</option>)}
                  </Select>
                </Field>
                <Field label="Editorial Placement" hint="Featuring and promotion logic only.">
                  <Select value={form.editorial_placement} onChange={f('editorial_placement')} data-testid="editorial-placement-select">
                    {EDITORIAL_PLACEMENTS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
                  </Select>
                </Field>
                <Field label="Editorial Slot" hint="Newsroom Editorial Row. Setting this pins the item now — only the latest-pinned item per slot renders publicly.">
                  <Select value={form.editorial_slot} onChange={f('editorial_slot')} data-testid="editorial-slot-select">
                    {EDITORIAL_SLOTS.map(s => <option key={s} value={s}>{EDITORIAL_SLOT_LABELS[s]}</option>)}
                  </Select>
                  {form.editorial_slot && form.editorial_pinned_at && (
                    <p className="text-xs text-gray-400 mt-1">Pinned: {new Date(form.editorial_pinned_at).toLocaleString()}</p>
                  )}
                </Field>
                <Field label="Website Section" hint="Where this item belongs on the website.">
                  <Select value={form.website_section} onChange={f('website_section')} data-testid="website-section-select">
                    <option value="">— None —</option>
                    {WEBSITE_SECTIONS.map(s => <option key={s} value={s}>{SECTION_LABELS[s] || s}</option>)}
                  </Select>
                </Field>
                <Field label="Magazine Status" hint="Content eligibility only. Actual issue/page placement stays in Magazine Manager.">
                  <Select value={form.magazine_status} onChange={f('magazine_status')} data-testid="magazine-status-select">
                    {MAGAZINE_STATUSES_LIST.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </Select>
                </Field>
              </div>

              <Field label="Distribution Targets" hint="Where this item is allowed to appear. Select all that apply.">
                <div className="flex flex-wrap gap-4 mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="distribution-targets">
                  {DISTRIBUTION_TARGETS.map(target => (
                    <label key={target} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={(form.distribution_targets || []).includes(target)}
                        onChange={e => {
                          const current = form.distribution_targets || [];
                          const updated = e.target.checked ? [...current, target] : current.filter(t => t !== target);
                          setForm(prev => ({ ...prev, distribution_targets: updated }));
                        }}
                        className="w-4 h-4"
                        data-testid={`dist-target-${target}`}
                      />
                      <span className="text-sm text-gray-700 capitalize">{target.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Editorial Topics" hint="Structured internal topic taxonomy. Select all that apply.">
                <div className="flex flex-wrap gap-3 mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="editorial-topics">
                  {EDITORIAL_TOPICS_LIST.map(topic => (
                    <label key={topic} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={(form.editorial_topics || []).includes(topic)}
                        onChange={e => {
                          const current = form.editorial_topics || [];
                          const updated = e.target.checked ? [...current, topic] : current.filter(t => t !== topic);
                          setForm(prev => ({ ...prev, editorial_topics: updated }));
                        }}
                        className="w-4 h-4"
                        data-testid={`topic-${topic}`}
                      />
                      <span className="text-sm text-gray-700">{topic.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Tags" hint="Flexible reader-facing keywords. Comma-separated.">
                <Input value={form.tags} onChange={f('tags')} placeholder="careers, healthcare, signal" />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Trending Score">
                  <Input value={form.trending_score} onChange={f('trending_score')} type="number" placeholder="0.0 – 100.0" />
                </Field>
                <Field label="Homepage Featured Rank" hint="Lower number = higher priority">
                  <Input value={form.homepage_featured_rank} onChange={f('homepage_featured_rank')} type="number" placeholder="1–99" />
                </Field>
              </div>
              <div className="flex gap-6 pt-1">
                <Toggle checked={form.is_editors_choice} onChange={b('is_editors_choice')} label="Editor's Choice" />
                <Toggle checked={form.is_trending} onChange={b('is_trending')} label="Trending" />
              </div>
            </Section>

            {/* Flags / Codes */}
            <Section title="Flags / Codes">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <Toggle checked={form.me} onChange={b('me')} label="ME — Magazine Editorial Locked" />
                  <p className="text-xs text-gray-400 mt-1">Set when article is assigned to a magazine issue</p>
                </div>
                <div>
                  <Toggle checked={form.nl} onChange={b('nl')} label="NL — Newsletter Flagged" />
                  <p className="text-xs text-gray-400 mt-1">Marks article for newsletter distribution</p>
                </div>
                <Field label="ORG — Organization Code">
                  <Input value={form.org} onChange={f('org')} placeholder="OP" data-testid="content-org-input" />
                </Field>
              </div>
            </Section>

            {/* SEO */}
            <Section title="SEO">
              <div className="grid grid-cols-2 gap-4">
                <Field label="SEO Title">
                  <Input value={form.seo_title} onChange={f('seo_title')} placeholder="Custom meta title" />
                </Field>
                <Field label="OG Image URL">
                  <Input value={form.og_image_url} onChange={f('og_image_url')} placeholder="https://…" />
                </Field>
              </div>
              <Field label="SEO Description">
                <Textarea value={form.seo_description} onChange={f('seo_description')} rows={2} placeholder="Meta description (150–160 chars)" />
              </Field>
              <Field label="Canonical URL">
                <Input value={form.canonical_url} onChange={f('canonical_url')} placeholder="https://opportunitypost.com/article/…" />
              </Field>
            </Section>

            {/* Lifecycle / Urgency */}
            <Section title="Lifecycle / Urgency">
              <div className="grid grid-cols-3 gap-4">
                <Field label="Live Window (days)" hint="Content expires after this many days post-publish.">
                  <Input value={form.live_window_days} onChange={f('live_window_days')} type="number" />
                </Field>
                <Field label="Countdown Start (days)" hint="Show countdown badge when this many days remain.">
                  <Input value={form.countdown_start_days} onChange={f('countdown_start_days')} type="number" />
                </Field>
                <Field label="Scheduled For">
                  <Input value={form.scheduled_for} onChange={f('scheduled_for')} type="datetime-local" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Expired Visibility">
                  <Select value={form.expired_visibility} onChange={f('expired_visibility')}>
                    {EXPIRED_VISIBILITY.map(v => <option key={v} value={v}>{v}</option>)}
                  </Select>
                </Field>
              </div>
              <Toggle checked={form.show_countdown} onChange={b('show_countdown')} label="Show Countdown Badge" />
            </Section>

          </div>{/* end right column */}

        </div>{/* end two-column grid */}

        {/* ─── FULL WIDTH: Legacy / Deprecated Fields ─── */}
        <details className="bg-white rounded-xl border border-amber-200 mb-4 overflow-hidden">
          <summary className="px-6 py-4 cursor-pointer select-none flex items-center justify-between" style={{ listStyle: 'none' }} data-testid="deprecated-fields-toggle">
            <span className="text-sm font-semibold text-amber-700 uppercase tracking-wider">Legacy / Deprecated Fields</span>
            <span className="text-xs text-amber-500 font-normal">Preserved for existing records — do not use for new content</span>
          </summary>
          <div className="px-6 pb-6 pt-0 border-t border-amber-100">
            <p className="text-xs text-amber-600 bg-amber-50 rounded p-2 mb-4 mt-3">
              These fields are deprecated. New content should use Core Taxonomy fields above. Existing records retain their values here for backward compatibility.
            </p>
            <div className="grid grid-cols-2 gap-4 opacity-70">
              <Field label="Section (Legacy — print section label)">
                <Input value={form.section} onChange={f('section')} placeholder="e.g. Cover Story, Departments" />
              </Field>
              <Field label="Department (Deprecated — use Website Section)">
                <Input value={form.department} onChange={f('department')} placeholder="e.g. Editorial, Intelligence" />
              </Field>
              <Field label="Editorial Rail (Deprecated — use Editorial Placement)">
                <Select value={form.editorial_rail} onChange={f('editorial_rail')}>
                  <option value="">— None —</option>
                  {RAILS.map(r => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
              <Field label="Anchor Taxonomy (Deprecated — use Editorial Topics)">
                <Input value={form.anchor_taxonomy} onChange={f('anchor_taxonomy')} placeholder="workforce, policy" />
              </Field>
            </div>
            <Field label="Issue IDs (Deprecated — use Magazine Manager for issue placement)">
              <Input value={form.issue_ids} onChange={f('issue_ids')} placeholder="uuid1, uuid2" />
            </Field>
          </div>
        </details>

        {/* ─── FULL WIDTH: Relationships ─── */}
        <Section title="Relationships">
          <Field label="Related Content IDs" hint="Comma-separated content item IDs for linking related articles.">
            <Input value={form.related_content_ids} onChange={f('related_content_ids')} placeholder="uuid1, uuid2" />
          </Field>
        </Section>

        {/* Save footer */}
        <div className="flex justify-end gap-3 pb-8">
          <button onClick={() => navigate('/admin/content')} className="px-6 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            data-testid="save-content-footer-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--op-navy, #1a2744)' }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Item'}
          </button>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
}
